import React, { useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Check, Loader2, Star, ThumbsDown, ThumbsUp } from "lucide-react";
import { UrlContext } from "../contexts/UrlContext";
import { ClientContext } from "../contexts/ClientContext";
import ClientError from "../components/Client/SharedProfiles/clientError";
import ConsultantLoading from "../components/Client/SharedProfiles/ConsultantLoading";
import expertDefaultImg from "../images/expert-default.png";
import logoWhite from "../images/eph-logo_10yrs.png";
import logoMark from "../images/logo_white.png";
import globarWinner from "../images/Global_100.png";
import leadingInnovator from "../images/Leading_Innovator.png";
import trustedTalent from "../images/Trusted_Talent.png";


const MEDIA_LINK = "https://media.licdn.com/";
const DATABASE_LINK = "https://images.expertpowerhouse.com";
const pictureSrc = (p) => (!p ? expertDefaultImg : p.includes(MEDIA_LINK) ? p : `${DATABASE_LINK}${p}`);
const RATING_LABEL = ["", "Poor", "Fair", "Good", "Very good", "Excellent"];
const OVERALL_MAX = 2000;
const EXPERT_MAX = 1000;

const card = "rounded-2xl border border-slate-200 bg-white shadow-[0_10px_28px_rgba(15,23,42,0.08)]";
const textareaClass =
  "w-full resize-y rounded-xl border border-slate-300 bg-white px-3.5 py-2.5 text-[15px] leading-relaxed text-slate-800 placeholder:text-slate-500 transition focus:border-[#365561] focus:outline-none focus:ring-2 focus:ring-[#365561]/20 disabled:bg-slate-50";

function Stars({ value, onChange, label, size = 32 }) {
  const [hover, setHover] = useState(0);
  const shown = hover || value || 0;
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div role="radiogroup" aria-label={label} className="flex" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            role="radio"
            aria-checked={value === n}
            aria-label={`${n} star${n > 1 ? "s" : ""}, ${RATING_LABEL[n]}`}
            onMouseEnter={() => setHover(n)}
            onClick={() => onChange(value === n ? null : n)}
            className="flex h-11 w-11 items-center justify-center rounded-full transition hover:bg-amber-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#365561]/40 motion-reduce:transition-none"
          >
            <Star
              size={size}
              strokeWidth={1.5}
              className={`transition-colors motion-reduce:transition-none ${n <= shown ? "fill-amber-400 text-amber-400" : "fill-transparent text-slate-300"}`}
            />
          </button>
        ))}
      </div>
      <span className="min-w-[5.5rem] text-sm font-medium text-slate-600" aria-live="polite">
        {RATING_LABEL[shown]}
      </span>
    </div>
  );
}

function Counter({ length, max }) {
  return (
    <span className={`text-xs tabular-nums ${length > max * 0.9 ? "text-amber-700" : "text-slate-500"}`}>
      {length}/{max}
    </span>
  );
}

function useBubbleCount() {
  const [count, setCount] = useState(30);

  useEffect(() => {
    const updateCount = () => {
      // 1 dot per 45px, with a small minimum
      setCount(Math.max(8, Math.floor(window.innerWidth / 45)));
    };

    updateCount();
    window.addEventListener("resize", updateCount);
    return () => window.removeEventListener("resize", updateCount);
  }, []);

  return count;
}

const rowOffsets = [0, 2, 3];
const imgClass = "h-[30px] sm:h-[34px] md:h-[36px] xl:h-[46px] print-eph-logo transform transition duration-300 ease-in-out hover:scale-150";

// Same banner as the shared profiles page (shareProfileHeader.jsx).
function ClientBanner() {
  const baseCount = useBubbleCount();
  const getRowCount = (rowIndex) => Math.max(baseCount - (rowOffsets[rowIndex] || 0), 0);

  return (
    <div className="print-banner relative h-20 sm:h-28 md:h-32 lg:h-36 bg-cover bg-top bg-[24%_0%] sm:bg-[23%_0%] rounded-t-[10px] print-rounded-none">
      <div className="absolute inset-x-0 top-0 h-20 sm:h-28 md:h-32 lg:h-36 bg-gradient-to-r from-[#365561] via-[#2f4a55] to-[#0f172a]" />

      {[0, 1].map((row) => (
        <div
          key={row}
          className="print-hide-bubbles absolute inset-0 flex flex-wrap justify-end gap-2 sm:gap-3 md:gap-4 px-4 opacity-80 top-[var(--mobile-top)] sm:top-[var(--sm)] md:top-[var(--md)] lg::top-[var(--lg)] xl:top-[var(--desktop-top)]"
          style={{
            "--mobile-top": `${24 + row * 22}px`,
            "--sm": `${24 + row * 28}px`,
            "--md": `${24 + row * 33}px`,
            "--lg": `${24 + row * 38}px`,
            "--desktop-top": `${24 + row * 42}px`,
          }}
        >
          {[...Array(getRowCount(row))].map((_, i) => {
            const isHighlight = i % 7 === 0 || i % 11 === 0;

            return (
              <div
                key={i}
                className={`rounded-full h-[12px] w-[12px] sm:h-[13px] sm:w-[13px] md:h-[14px] md:w-[14px] lg:h-[15px] lg:w-[15px] xl:h-[16px] xl:w-[16px] ${
                  isHighlight ? "bg-[#2dd4bf]" : "bg-[#3b6a75]"
                }`}
              />
            );
          })}
        </div>
      ))}

      <div className="absolute top-4 left-4 text-white text-xl p-2 rounded-full transition-transform duration-200 ease-in-out">
        <img
          src={logoWhite}
          alt="EPH Logo"
          className="w-[140px] sm:w-[160px] md:w-[190px] lg:w-[210px] xl:w-[240px] print-eph-logo"
        />
      </div>

      <div className="hidden sm:block absolute bottom-0 right-4 text-white text-xl p-2 rounded-full">
        <div className="flex flex-row gap-2">
          <img src={globarWinner} alt="" className={imgClass}/>
          <img src={leadingInnovator} alt="" className={imgClass}/>
          <img src={trustedTalent} alt="" className={imgClass}/>
          <img src="https://cdn.prod.website-files.com/66ac4fc3fadbc793706d2f8c/66ad211ca086377aa08e2f56_eph-awards-05.png" alt="" className={imgClass}/>
          <img src="https://cdn.prod.website-files.com/66ac4fc3fadbc793706d2f8c/66ad21149adf634bc414c9ab_eph-awards-07.png" alt="" className={imgClass}/>
          <img src="https://cdn.prod.website-files.com/66ac4fc3fadbc793706d2f8c/66cda1bb911ba87dfbd48852_eph-awards.png" alt="" className={imgClass}/>
          <img src="https://cdn.prod.website-files.com/66ac4fc3fadbc793706d2f8c/66ad21045c28cc4679e24598_eph-awards-03.png" alt="" className={imgClass}/>
        </div>
      </div>
    </div>
  );
}

function PageShell({ projectName, children }) {
  return (
    <div className="min-h-screen bg-gray-100">
      <ClientBanner />
      <div className="flex h-12 items-center justify-center px-[16px] text-[#365561]">
        {projectName && <p className="min-w-0 truncate text-base font-semibold sm:text-lg lg:text-xl">{projectName}</p>}
      </div>
      {children}
    </div>
  );
}

export default function ClientFeedback() {
  const { FEEDBACK_API_URL } = useContext(UrlContext);
  const { isAuthenticated, error, loading } = useContext(ClientContext);
  const { projectID } = useParams();
  const code = localStorage.getItem("clientToken");

  const [page, setPage] = useState({ state: "loading" }); // loading | ready | invalid | closed | thanks
  const [overall, setOverall] = useState({ overallRating: null, overallComment: "", wouldRecommend: null });
  const [experts, setExperts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  useEffect(() => {
    if (!isAuthenticated) return;
    fetch(`${FEEDBACK_API_URL}public/feedback/${projectID}/load`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code }),
    })
      .then(async (res) => {
        const data = await res.json();
        if (res.status === 410) return setPage({ state: "closed" });
        if (!res.ok) return setPage({ state: "invalid" });
        // Answers are final once submitted: reopening the link shows the confirmation, not the form
        if (data.overall?.submittedAt) return setPage({ state: "thanks", projectName: data.projectName, earlier: true });
        setExperts(data.experts.map((e) => ({ ...e, rating: null, comment: "" })));
        // later rounds are experts-only: the client already rated expertpowerhouse
        setPage({ state: "ready", projectName: data.projectName, askOverall: data.askOverall !== false });
      })
      .catch(() => setPage({ state: "invalid" }));
  }, [isAuthenticated, projectID]);

  const updateExpert = (id, patch) =>
    setExperts((prev) => prev.map((e) => (e.expertID === id ? { ...e, ...patch } : e)));

  const submit = async (ev) => {
    ev.preventDefault();
    setSaving(true);
    setSaveError("");
    try {
      const res = await fetch(`${FEEDBACK_API_URL}public/feedback/${projectID}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          code,
          ...overall,
          experts: experts.map((e) => ({ expertID: e.expertID, rating: e.rating, comment: e.comment || null })),
        }),
      });
      const data = await res.json();
      if (res.status === 410) setPage({ state: "closed" });
      else if (res.status === 409) setPage((p) => ({ ...p, state: "thanks", earlier: true }));
      else if (!res.ok) setSaveError(data.msg || "Your feedback was not saved. Please try again.");
      else {
        setPage((p) => ({ ...p, state: "thanks" }));
        window.scrollTo({ top: 0 });
      }
    } catch {
      setSaveError("Your feedback was not saved. Check your connection and try again.");
    }
    setSaving(false);
  };

  if (loading) return <ConsultantLoading />;
  if (!isAuthenticated) return <ClientError msg={error} />;
  if (page.state === "loading") return <ConsultantLoading />;
  if (page.state === "invalid") return <ClientError msg="This link is not valid" />;
  if (page.state === "closed") return <ClientError msg="Feedback is closed. Thank you." />;

  if (page.state === "thanks") {
    return (
      <PageShell projectName={page.projectName}>
        <main className="mx-auto max-w-xl px-[16px] pb-16 pt-6 sm:pt-10">
          <section className={`${card} px-6 py-10 text-center`} role="status">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
              <Check size={28} strokeWidth={2.5} aria-hidden="true" />
            </span>
            <h1 className="mt-[16px] text-2xl font-semibold text-slate-900">Thank you for your feedback</h1>
            <p className="mx-auto mt-2 max-w-sm text-[15px] leading-relaxed text-slate-600">
              {page.earlier
                ? "We have already received your feedback for this project and shared it with the expertpowerhouse team."
                : "It has been shared with the expertpowerhouse team."}
            </p>
          </section>
        </main>
      </PageShell>
    );
  }

  // Two independent tasks: rating expertpowerhouse (left, pinned) and rating each expert (right).
  // Side by side from lg up; stacked below, and a single column when there are no experts.
  const split = experts.length > 0 && page.askOverall;
  const hasAnswer =
    (page.askOverall &&
      (overall.overallRating != null || overall.wouldRecommend != null || overall.overallComment.trim() !== "")) ||
    experts.some((e) => e.rating != null || e.comment.trim() !== "");
  const width = split ? "max-w-6xl" : "max-w-3xl";

  return (
    <PageShell projectName={page.projectName}>
      <form onSubmit={submit}>
        <fieldset disabled={saving} className="min-w-0">
          <main className={`mx-auto ${width} px-[16px] pb-8 pt-[16px] sm:pt-8`}>
            <h1 className="text-2xl font-semibold text-slate-900 sm:text-[28px]">
              {page.askOverall ? "How was your experience?" : "How did the experts do?"}
            </h1>
            <p className="mt-2 max-w-2xl text-[15px] leading-relaxed text-slate-600">
              {page.askOverall
                ? "Your answers go directly to the expertpowerhouse team that ran this project. Every question is optional."
                : "Thanks for your earlier feedback. This time we'd only like to hear about the experts on your recent work. Every question is optional."}
            </p>

            <div className={split ? "mt-8 grid grid-cols-[minmax(0,1fr)] gap-x-10 gap-y-10 lg:grid-cols-2 lg:items-start" : "mt-8"}>
            {page.askOverall && (
            <section className={split ? "lg:sticky lg:top-[24px]" : undefined} aria-labelledby="overall-heading">
              <h2 id="overall-heading" className="text-lg font-semibold text-slate-900">
                Working with expertpowerhouse
              </h2>
              <p className="mt-1 text-[15px] text-slate-600">Your experience with our team overall.</p>

              <div className={`${card} mt-[16px] grid overflow-hidden md:grid-cols-[110px_minmax(0,1fr)]`}>
              {/* same strip as the expert photos; the overflow crop shows only the logo's circle emblem */}
              <div className="hidden items-center justify-center bg-[#365561] md:flex" aria-hidden="true">
                <div className="h-[64px] w-[61px] overflow-hidden">
                  <img src={logoMark} alt="" className="h-full w-auto max-w-none" />
                </div>
              </div>
              <div className="min-w-0 p-[16px] sm:p-5">
              {/* mirrors an expert card row for row (name, stars, 2-line note) so both columns line up */}
              <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
                <p className="min-w-0 truncate text-base font-semibold text-slate-900 md:text-[17px]">expertpowerhouse</p>
                {/* 44px tap targets; the negative margin keeps the row as tall as an expert's name row */}
                <div role="radiogroup" aria-labelledby="recommend-label" className="flex shrink-0 items-center gap-1 md:-my-[9px]">
                  <span id="recommend-label" className="mr-1 text-sm font-medium text-slate-600">
                    Recommend us?
                  </span>
                  {[
                    ["Yes, I would recommend you", true, ThumbsUp],
                    ["No, I would not recommend you", false, ThumbsDown],
                  ].map(([label, val, Icon]) => {
                    const active = overall.wouldRecommend === val;
                    return (
                      <button
                        key={label}
                        type="button"
                        role="radio"
                        aria-checked={active}
                        aria-label={label}
                        title={label}
                        onClick={() => setOverall((o) => ({ ...o, wouldRecommend: active ? null : val }))}
                        className={`flex h-11 w-11 items-center justify-center rounded-full border transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#365561]/40 motion-reduce:transition-none ${
                          active
                            ? "border-[#365561] bg-[#365561] text-white"
                            : "border-slate-300 bg-white text-slate-700 hover:border-[#365561]/60 hover:text-[#365561]"
                        }`}
                      >
                        <Icon size={18} aria-hidden="true" />
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="mt-2 -ml-1.5">
                <Stars
                  label="Overall rating"
                  size={26}
                  value={overall.overallRating}
                  onChange={(v) => setOverall((o) => ({ ...o, overallRating: v }))}
                />
              </div>
              <div className="mt-[12px]">
                <textarea
                  rows={2}
                  maxLength={OVERALL_MAX}
                  aria-label="Comment for expertpowerhouse"
                  placeholder="What went well, and what could we do better? (optional)"
                  className={textareaClass}
                  value={overall.overallComment}
                  onChange={(e) => setOverall((o) => ({ ...o, overallComment: e.target.value }))}
                />
                <div className="mt-1 flex justify-end">
                  <Counter length={overall.overallComment.length} max={OVERALL_MAX} />
                </div>
              </div>
              </div>
              </div>
            </section>
            )}

            {experts.length > 0 && (
              <section aria-labelledby={page.askOverall ? "experts-heading" : undefined} aria-label={page.askOverall ? undefined : "Experts"}>
                {page.askOverall && (
                  <>
                    <h2 id="experts-heading" className="text-lg font-semibold text-slate-900">
                      The experts on your project
                    </h2>
                    <p className="mt-1 text-[15px] text-slate-600">Rate anyone you worked with. Skip the rest.</p>
                  </>
                )}

                <ul className={`${page.askOverall ? "mt-[16px]" : ""} space-y-3`}>
                  {experts.map((e) => (
                    <li key={e.expertID} className={`${card} grid overflow-hidden md:grid-cols-[110px_minmax(0,1fr)]`}>
                      <img
                        src={pictureSrc(e.picture)}
                        alt=""
                        className="hidden h-full min-h-[150px] w-[110px] bg-[#365561] object-cover md:block"
                        onError={(ev) => {
                          ev.currentTarget.onerror = null;
                          ev.currentTarget.src = expertDefaultImg;
                        }}
                      />
                      <div className="min-w-0 p-[16px] sm:p-5">
                        <div className="flex items-center gap-3">
                          <img
                            src={pictureSrc(e.picture)}
                            alt=""
                            className="h-11 w-11 shrink-0 rounded-full bg-slate-100 object-cover md:hidden"
                            onError={(ev) => {
                              ev.currentTarget.onerror = null;
                              ev.currentTarget.src = expertDefaultImg;
                            }}
                          />
                          <p className="min-w-0 truncate text-base font-semibold text-slate-900 md:text-[17px]">{e.name}</p>
                        </div>
                        <div className="mt-2 -ml-1.5">
                          <Stars
                            label={`Rating for ${e.name}`}
                            size={26}
                            value={e.rating}
                            onChange={(v) => updateExpert(e.expertID, { rating: v })}
                          />
                        </div>
                        <div className="mt-[12px]">
                          <textarea
                            rows={2}
                            maxLength={EXPERT_MAX}
                            aria-label={`Comment for ${e.name}`}
                            placeholder={`A note about ${e.name.split(" ")[0]} (optional)`}
                            className={textareaClass}
                            value={e.comment}
                            onChange={(ev) => updateExpert(e.expertID, { comment: ev.target.value })}
                          />
                          <div className="mt-1 flex justify-end">
                            <Counter length={e.comment.length} max={EXPERT_MAX} />
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}
            </div>
          </main>

          <div className="sticky bottom-0 border-t border-slate-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/80">
            <div className={`mx-auto flex ${width} flex-col gap-3 px-[16px] py-[12px] sm:flex-row sm:items-center sm:justify-between`}>
              {saveError ? (
                <p role="alert" className="text-sm font-medium text-red-700">
                  {saveError}
                </p>
              ) : (
                <p className="text-sm text-slate-600">
                  {hasAnswer ? "Answers can't be changed after you submit." : "Answer at least one question to submit."}
                </p>
              )}
              <button
                type="submit"
                disabled={!hasAnswer}
                className="inline-flex min-h-[44px] shrink-0 items-center justify-center gap-2 rounded-full bg-[#365561] px-6 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(54,85,97,0.25)] transition hover:bg-[#2c4650] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#365561]/40 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving && <Loader2 size={16} className="animate-spin motion-reduce:animate-none" aria-hidden="true" />}
                {saving ? "Saving" : "Submit feedback"}
              </button>
            </div>
          </div>
        </fieldset>
      </form>
    </PageShell>
  );
}
